import { StatusTag } from "@/views/components/StatusTag";
import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { Button, Card, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Page, InstallPromotionCommissionVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Table as ZhTable,
    type TableColumnProps,
    Form as ZhFilterForm,
    Button as ZhFilterButton,
    Input as ZhFilterInput,
    Select as ZhFilterSelect
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { INSTALL_PROMOTION_COMMISSION_STATE } from "@/meta/expenseManage/installPromotionCommission";
import { WAYBILL_STATE } from "@/meta/logistics/waybill";
import { useUserStore } from "@/stores/react/authStore";

type CommissionRow = Partial<InstallPromotionCommissionVo>;
export interface InstallPromotionCommissionPageQuery {
    page: number;
    size: number;
    code?: string;
    userInfo?: string;
    shipName?: string;
    state?: number;
    beginTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: "asc" | "desc";
}

export interface InstallPromotionCommissionPageProps {
    loadCommissions?: (query: InstallPromotionCommissionPageQuery) => Promise<Page<CommissionRow>>;
    permissions?: string[];
}

/** 复用 Vue 费用管理安装推广佣金分页接口。 */
export async function loadInstallPromotionCommissionPage(
    query: InstallPromotionCommissionPageQuery
): Promise<Page<CommissionRow>> {
    const response = await request.service.get<Page<CommissionRow>>("/install-promotion-commission/paging", {
        params: query
    });
    return response.data;
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const formatCustomer = (value?: { institutionName?: string; realName?: string; mobile?: string; code?: string }) =>
    value
        ? `${value.institutionName || value.realName || DEFAULT_VALUE} | ${value.mobile || DEFAULT_VALUE} | ${value.code || DEFAULT_VALUE}`
        : DEFAULT_VALUE;

/** 费用管理-安装推广佣金列表，保留 Vue 首屏筛选、分页和字段契约。 */
export function InstallPromotionCommissionPage({
    loadCommissions = loadInstallPromotionCommissionPage,
    permissions
}: InstallPromotionCommissionPageProps) {
    const storePermissions = useUserStore(user => user.userInfo.permissions || []);
    const banButton = useUserStore(user => user.banButton);
    // 保持 Vue v-auth 语义：显式测试权限优先，否则读取当前登录用户权限。
    const canSettle = (permissions ?? storePermissions).includes("ROLE_DEVICE_BONUS_W");
    const [rows, setRows] = useState<CommissionRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<InstallPromotionCommissionPageQuery>({ page: 1, size: 10 });
    const [code, setCode] = useState("");
    const [userInfo, setUserInfo] = useState("");
    const [shipName, setShipName] = useState("");
    const [state, setState] = useState<number | undefined>();
    const [settlementTimeStart, setSettlementTimeStart] = useState("");
    const [settlementTimeEnd, setSettlementTimeEnd] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        void loadCommissions(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("安装推广佣金加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadCommissions, query]);

    // 将 Vue ProTable 的草稿筛选值转换为分页请求，并回到第一页。
    const submitFilters = () => {
        const nextQuery: InstallPromotionCommissionPageQuery = { page: 1, size: query.size };
        if (code) nextQuery.code = code;
        if (userInfo) nextQuery.userInfo = userInfo;
        if (shipName) nextQuery.shipName = shipName;
        if (state !== undefined) nextQuery.state = state;
        // 对齐 Vue getTableList：日期范围字段不提交 settlementTime，改为 beginTime/endTime。
        if (settlementTimeStart) nextQuery.beginTime = settlementTimeStart;
        if (settlementTimeEnd) nextQuery.endTime = settlementTimeEnd;
        setQuery(nextQuery);
    };

    const resetFilters = () => {
        setCode("");
        setUserInfo("");
        setShipName("");
        setState(undefined);
        setSettlementTimeStart("");
        setSettlementTimeEnd("");
        setQuery({ page: 1, size: 10 });
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 佣金列表的字段、状态和结算操作。
    const columns: TableColumnProps<CommissionRow>[] = [
        {
            key: "id",
            label: "编号",
            width: 144,
            prop: "commission",
            render: ({ row }) =>
                row.commission?.id == null ? (
                    DEFAULT_VALUE
                ) : (
                    <Link to={`/expenseManagement/installPromotion/detail/${row.commission.id}`}>
                        {row.commission.id}
                    </Link>
                )
        },
        {
            key: "promoter",
            label: "推介人账号",
            minWidth: 200,
            prop: "commission",
            render: ({ row }) => formatCustomer(row.commission?.promoterInfo)
        },
        {
            key: "ship",
            label: "船舶",
            minWidth: 148,
            prop: "commission",
            render: ({ row }) =>
                `${row.commission?.shipCnname || DEFAULT_VALUE}/${row.commission?.shipName || DEFAULT_VALUE}`
        },
        {
            key: "orderShipId",
            label: "船运单",
            width: 218,
            prop: "commission",
            render: ({ row }) => {
                const orderShipId = row.commission?.orderShipId;
                if (orderShipId == null) return DEFAULT_VALUE;

                // 对齐 Vue 列表插槽：运单编号可进入详情，并同时展示当前运单状态。
                return (
                    <>
                        <Link to={`/logistics/shippingNeed/waybillManagementInfo/${orderShipId}`}>{orderShipId}</Link>（
                        {WAYBILL_STATE.getIntroByCode(row.orderShipState) || DEFAULT_VALUE}）
                    </>
                );
            }
        },
        {
            key: "shipInfoServiceFeeId",
            label: "船务信息服务费",
            width: 140,
            prop: "commission",
            render: ({ row }) => row.commission?.shipInfoServiceFeeId || DEFAULT_VALUE
        },
        {
            key: "commissionAmount",
            label: "结佣金额（￥）",
            width: 140,
            prop: "commission",
            render: ({ row }) => row.commission?.commissionAmount ?? DEFAULT_VALUE
        },
        {
            key: "settlementTime",
            label: "结算时间",
            width: 160,
            prop: "commission",
            render: ({ row }) =>
                row.commission?.state === INSTALL_PROMOTION_COMMISSION_STATE.SETTLED.code
                    ? formatDate(row.commission.settlementTime)
                    : DEFAULT_VALUE
        },
        {
            key: "createdTime",
            label: "创建时间",
            width: 160,
            prop: "commission",
            sortable: true,
            render: ({ row }) => formatDate(row.commission?.createdTime)
        },
        {
            key: "state",
            label: "状态",
            width: 90,
            prop: "commission",
            render: ({ row }) => (
                <StatusTag>
                    {INSTALL_PROMOTION_COMMISSION_STATE.getIntroByCode(row.commission?.state) || DEFAULT_VALUE}
                </StatusTag>
            )
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 60,
            prop: "commission",
            render: ({ row }) => {
                const commission = row.commission;
                const canSettleRow =
                    commission?.state !== INSTALL_PROMOTION_COMMISSION_STATE.SETTLED.code &&
                    row.orderShipState === WAYBILL_STATE.COMPLETED.code;
                if (!canSettle || !canSettleRow || !commission?.id) return null;
                // Vue 在 ROLE_BUTTON_BAN 下保留入口但禁止跳转，避免禁用态绕过权限导航。
                return banButton ? (
                    <Button aria-label="结算" disabled type="link">
                        结算
                    </Button>
                ) : (
                    <Link to={`/expenseManagement/installPromotion/settle/${commission.id}`}>结算</Link>
                );
            }
        }
    ];

    return (
        <section aria-label="install-promotion-commission-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<CommissionRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    rowKey={row => String(row.commission?.id || row.virtualId || "commission")}
                    onSortChange={({ prop, order }) => {
                        if (prop !== "commission") return;
                        setQuery(current => {
                            const nextQuery: InstallPromotionCommissionPageQuery = { ...current, page: 1 };
                            delete nextQuery.sortKey;
                            delete nextQuery.sortOrder;
                            if (order) {
                                nextQuery.sortKey = "created_time";
                                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                            }
                            return nextQuery;
                        });
                    }}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                安装推广佣金
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <ZhFilterForm
                                className="admin-table-filter-form mb-4"
                                layout="inline"
                                onFinish={submitFilters}
                            >
                                <ZhFilterForm.Item>
                                    <ZhFilterInput
                                        aria-label="编号"
                                        maxLength={50}
                                        placeholder="请输入编号、船运单编号、船务信息服务费编号搜索"
                                        style={{ width: 300 }}
                                        value={code}
                                        onChange={event => setCode(event.target.value)}
                                    />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item>
                                    <ZhFilterInput
                                        aria-label="推介人账号"
                                        maxLength={50}
                                        placeholder="请输入账号信息搜索"
                                        value={userInfo}
                                        onChange={event => setUserInfo(event.target.value)}
                                    />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item>
                                    <ZhFilterInput
                                        aria-label="船舶名称"
                                        placeholder="请输入船舶名称搜索"
                                        value={shipName}
                                        onChange={event => setShipName(event.target.value)}
                                    />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item>
                                    <ZhFilterSelect
                                        aria-label="状态"
                                        allowClear
                                        placeholder="请选择状态"
                                        value={state}
                                        options={INSTALL_PROMOTION_COMMISSION_STATE.options.map(option => ({
                                            label: option.label,
                                            value: Number(option.value)
                                        }))}
                                        onChange={setState}
                                    />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item>
                                    <ZhDateRangePicker
                                        aria-label="结算时间"
                                        clearable
                                        endPlaceholder="结算结束日期"
                                        rangeSeparator="-"
                                        startPlaceholder="结算开始日期"
                                        type="daterange"
                                        value={
                                            settlementTimeStart || settlementTimeEnd
                                                ? ([settlementTimeStart || null, settlementTimeEnd || null] as [
                                                      string | null,
                                                      string | null
                                                  ])
                                                : null
                                        }
                                        valueFormat="YYYY-MM-DD"
                                        onChange={(_value, formattedValue) => {
                                            setSettlementTimeStart(formattedValue?.[0] || "");
                                            setSettlementTimeEnd(formattedValue?.[1] || "");
                                        }}
                                    />
                                </ZhFilterForm.Item>
                                <Space>
                                    <ZhFilterButton
                                        aria-label="查询"
                                        htmlType="submit"
                                        icon={<TableSearchIcon name="search" />}
                                        type="primary"
                                    >
                                        搜索
                                    </ZhFilterButton>
                                    <ZhFilterButton
                                        aria-label="重置"
                                        className="table-filter-reset-button"
                                        icon={<TableSearchIcon name="reset" />}
                                        title="重置"
                                        onClick={resetFilters}
                                    />
                                </Space>
                            </ZhFilterForm>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                total={total}
                                hideOnSinglePage
                                showSizeChanger
                                onChange={(page, size) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: size !== current.size ? 1 : page,
                                        size
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}
